import { useEffect, useRef, useState } from 'react';
import { useOSTheme } from './OSThemeContext';

// Force Graph'ı dinamik olarak yüklemek için Hook
function useForceGraphScript() {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if ((window as any).ForceGraph) {
      setLoaded(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/force-graph';
    script.async = true;
    script.onload = () => setLoaded(true);
    document.body.appendChild(script);
    return () => {
      // document.body.removeChild(script); // Gereksiz remove etmiyoruz cache'de kalsin
    };
  }, []);
  return loaded;
}

interface ForceGraphViewerProps {
  onNodeHover?: (node: any) => void;
}

export default function ForceGraphViewer({ onNodeHover }: ForceGraphViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const graphRef = useRef<any>(null);
  const isLoaded = useForceGraphScript();
  const { theme } = useOSTheme();
  const isDark = theme === 'dark';

  useEffect(() => {
    if (!isLoaded || !containerRef.current) {return;}

    const N_nodes = 25; // Node sayısını artırdık ki ağ dolsun (Mockup stili)
    const colors = ['#0ea5e9', '#9333ea', '#f43f5e', '#10b981', '#f59e0b']; // Canlı mockup renkleri
    const labels = ['Agentic_OS', 'Cloudflare', 'Azure', 'Force_Graph', 'CodePen', 'AI_Brain', 'Ops', 'Memory'];
    
    const gData = {
      nodes: [...Array(N_nodes).keys()].map(i => ({ 
        id: i, 
        name: labels[Math.floor(Math.random()*labels.length)], 
        val: Math.random() * 8 + 3, 
        color: colors[Math.floor(Math.random()*colors.length)] 
      })),
      links: [...Array(N_nodes * 2.5).keys()].map(() => ({ 
        source: Math.floor(Math.random()*N_nodes), 
        target: Math.floor(Math.random()*N_nodes) 
      }))
    };

    const ForceGraph = (window as any).ForceGraph;
    
    const Graph = ForceGraph()(containerRef.current)
      .graphData(gData)
      .backgroundColor('transparent')
      .linkColor(() => isDark ? 'rgba(0, 215, 244, 0.2)' : 'rgba(147, 51, 234, 0.4)')
      .linkWidth(2)
      .linkDirectionalParticles(4)
      .linkDirectionalParticleWidth(3)
      .linkDirectionalParticleColor(() => isDark ? '#00d7f4' : '#0ea5e9')
      .linkDirectionalParticleSpeed(0.006)
      .nodeCanvasObject((node: any, ctx: CanvasRenderingContext2D, globalScale: number) => {
        // Glow Efekti
        ctx.shadowBlur = isDark ? 15 : 10; 
        ctx.shadowColor = node.color; 
        ctx.fillStyle = node.color;
        ctx.beginPath(); 
        ctx.arc(node.x, node.y, Math.sqrt(node.val) * 1.8, 0, 2 * Math.PI, false); 
        ctx.fill();
        ctx.shadowBlur = 0;
        
        // Etiketler (Labels)
        if (globalScale > 1 || node.id % 2 === 0 || node.val > 6) {
            ctx.textAlign = 'center'; 
            ctx.textBaseline = 'middle'; 
            ctx.fillStyle = isDark ? '#e2e8f0' : '#0f172a';
            ctx.font = `bold ${12/globalScale}px "Space Grotesk", sans-serif`;
            
            const textWidth = ctx.measureText(node.name).width;
            
            // Etiket Arka Planı (Mockup stili: Glassy)
            ctx.fillStyle = isDark ? 'rgba(15, 23, 42, 0.8)' : 'rgba(255, 255, 255, 0.7)';
            ctx.fillRect(
                node.x - textWidth/2 - 2, 
                node.y + Math.sqrt(node.val)*1.8 + 6, 
                textWidth + 4, 
                14/globalScale
            );
            
            ctx.fillStyle = isDark ? '#e2e8f0' : '#0f172a';
            ctx.fillText(
                node.name, 
                node.x, 
                node.y + Math.sqrt(node.val) * 1.8 + 12 / globalScale + 7
            );
        }
      })
      .onNodeHover((node: any) => {
          if (containerRef.current) {
             containerRef.current.style.cursor = node ? 'pointer' : 'default';
          }
          if (onNodeHover) {onNodeHover(node);}
      });

    if ((window as any).d3) {
       Graph.d3Force('charge', (window as any).d3.forceManyBody().strength(-200));
       Graph.d3Force('link', (window as any).d3.forceLink().distance(70));
    }

    graphRef.current = Graph;

    const handleResize = () => {
      if (containerRef.current && graphRef.current) {
        graphRef.current.width(containerRef.current.clientWidth);
        graphRef.current.height(containerRef.current.clientHeight);
      }
    };

    window.addEventListener('resize', handleResize);
    setTimeout(handleResize, 100);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isLoaded, onNodeHover, isDark]);

  return <div ref={containerRef} style={{ width: '100%', height: '100%' }} />;
}
